import { brandHealthStats, categoryHealthStats } from '@/config/constants';
import {
    formatGrowth,
    formatPercentage,
    formatTimeSeries,
    formatVolume,
    renameCluster,
} from 'utils/helpers';
import { tooltips } from '@/copy';

export const formatNumberData = (key, dictionary) => {
    if (!(key in dictionary)) return null;
    const float = parseFloat(dictionary[key]);
    return isNaN(float) ? null : float;
};

const formatHealthStat = (stat, roundDown = false) => {
    const parsed = parseFloat(stat);
    return isNaN(parsed) ? null : roundDown ? Math.floor(parsed) : parsed;
};

const formatMetric = (metric, metricLabel) => {
    switch (metricLabel) {
        case 'market leaders':
            return typeof metric === 'number' ? metric : '-';
        case 'predicted growth':
            return formatGrowth(metric);
        case 'popularity convergence':
            return formatPercentage(metric);
        default:
            return formatVolume(metric);
    }
};

const priceSegmentationMap = [
    'LOW PRICE',
    'MEDIUM PRICE',
    'HIGH PRICE',
    null,
].reduce((acc, priceSegmentationKey) => {
    let scoreLabel;
    let score;

    switch (priceSegmentationKey) {
        case 'LOW PRICE':
            scoreLabel = 'Low';
            score = 0;
            break;
        case 'MEDIUM PRICE':
        case 'Medium Price':
            scoreLabel = 'Medium';
            score = 5;
            break;
        case 'HIGH PRICE':
            scoreLabel = 'High';
            score = 10;
            break;
        case 'na':
        default:
            scoreLabel = tooltips.healthStatsMissingScoreLabel;
            score = formatHealthStat(undefined, true);
    }

    acc[priceSegmentationKey || 'DEFAULT'] = {
        scoreLabel,
        score,
        metric: formatMetric(formatHealthStat(undefined)),
    };
    return acc;
}, {});

export const createHealthStat = (metricLabel, card_data) => {
    const { metric, score, scoreLabel } = healthStatColumnMap[metricLabel];

    if (metricLabel === 'price point') {
        return (
            priceSegmentationMap[card_data[scoreLabel]] ||
            priceSegmentationMap.DEFAULT
        );
    }

    const scoreValue = formatHealthStat(card_data[score], true);
    let scoreLabelShort;
    try {
        scoreLabelShort = getScoreLabels(
            metricLabel,
            scoreValue,
        ).scoreLabelShort;
    } finally {
        let scoreLabelValue = card_data[scoreLabel];
        if (scoreLabelValue === 'No Competition')
            scoreLabelValue = 'Very Low / No Competition';
        return {
            metric: formatMetric(
                formatHealthStat(card_data[metric]),
                metricLabel,
            ),
            score: scoreValue,
            scoreLabel:
                scoreLabelValue || tooltips.healthStatsMissingScoreLabel,
            scoreLabelShort,
        };
    }
};

const healthStatColumnMap = {
    'average monthly searches': {
        metric: 'Search Volume',
        score: 'Search Volume score',
        scoreLabel: 'Search Volume score_label',
    },
    'average brand searches': {
        metric: 'brands Volume',
        score: 'brands Volume score',
        scoreLabel: 'brands Volume score_label',
    },
    'market leaders': {
        metric: 'brand count',
        score: 'concentration score',
        scoreLabel: 'concentration score_label',
    },
    'price point': {
        metric: 'price',
        score: 'price_score',
        scoreLabel: 'price_segmentation',
    },
    'predicted growth': {
        metric: 'pred_YoY',
        score: 'pred_YoY_score',
        scoreLabel: 'pred_YoY score_label',
    },
};

export const shapeDataPoint = (dataPoint) => {
    const { category, brand, data, time_series, parent_trend_name } = dataPoint;
    const healthStats = (
        !!category && !brand ? categoryHealthStats : brandHealthStats
    ).reduce((acc, metricLabel) => {
        acc[metricLabel] = createHealthStat(metricLabel, data);
        return acc;
    }, {});
    return {
        ...(data && {
            price_segmentation: healthStats?.['price point']?.score,
            absoluteGrowth: formatNumberData('absolute growth', data),
            volume: formatNumberData('Search Volume', data),
            brandsVolume: data?.['brands Volume'],
            YoY: formatNumberData('YoY', data),
            QoQ: formatNumberData('QoQ', data),
            QoQ_increase: formatNumberData('QoQ_increase', data),
            MoM: formatNumberData('MoM', data),
            MoM_increase: formatNumberData('monthly absolute growth', data),
            predictedYoY: formatNumberData('pred_YoY', data),
            rank: formatNumberData(!!category ? 'cat_ranking' : 'rank', data),
            arrow: formatNumberData('arrow_YoY', data),
            cluster: 'cluster' in data ? renameCluster(data.cluster) : null,
            topAffinity: data.affinity_group || data.top_affinity_group,
            life_cycle: data?.life_cycle || null,
            healthStats,
        }),
        ...(time_series && {
            time_series: time_series.length
                ? formatTimeSeries(time_series)
                : time_series,
        }),
        id: dataPoint.id,
        ...(parent_trend_name && { parent_trend_name }),
    };
};

const getScoreLabel = (scoreLabels, score) => {
    for (const [scoreThreshold, label] of scoreLabels) {
        if (score > scoreThreshold || score === scoreThreshold) return label;
    }
};

const getSortedScoreLabels = (metricLabel, key) => {
    return Object.entries(tooltips.healthStats[metricLabel][key])
        .map(([score, label]) => [parseInt(score, 10), label])
        .sort(([a], [b]) => {
            return b - a;
        });
};

const getScoreLabels = (metricLabel, score) => {
    const scoreLabels = getSortedScoreLabels(metricLabel, 'scoreLabels');
    const scoreLabelsShort = getSortedScoreLabels(
        metricLabel,
        'scoreLabelsShort',
    );
    const scoreLabel =
        getScoreLabel(scoreLabels, score) ||
        tooltips.healthStatsMissingScoreLabel;
    const scoreLabelShort = getScoreLabel(scoreLabelsShort, score);
    return { scoreLabel, scoreLabelShort };
};

const getTikTokViewsScore = (metric) => {
    if (!metric && metric !== 0) return null;
    if (metric < 10000) return 1;
    if (metric < 100000) return 3;
    if (metric < 1000000) return 5;
    if (metric < 10000000) return 7;
    return 9;
};

const getTikTokViewsHealthStat = (data) => {
    if (!data) return null;
    const score = getTikTokViewsScore(data.non_cum_avg_weekly_views);
    return {
        ...getScoreLabels('average weekly views', score),
        score,
        metric: formatVolume(data.non_cum_avg_weekly_views),
    };
};

const getTikTokPostsScore = (metric) => {
    if (!metric && metric !== 0) return null;
    if (metric < 50) return 1;
    if (metric < 250) return 3;
    if (metric < 2000) return 5;
    if (metric < 10000) return 7;
    return 9;
};

const getTikTokPostsHealthStat = (data) => {
    if (!data) return null;
    const score = getTikTokPostsScore(data.total_posts_cnt);
    return {
        ...getScoreLabels('posts', score),
        score,
        metric: formatVolume(data.total_posts_cnt),
    };
};

const getEngagementScore = (metric) => {
    if (!metric && metric !== 0) return null;
    if (metric < 0.02) return 1;
    if (metric < 0.04) return 3;
    if (metric < 0.06) return 5;
    if (metric < 0.1) return 7;
    return 9;
};

const getEngagementHealthStat = (data) => {
    if (!data) return null;
    const score = getEngagementScore(data.engagement_prc);
    return {
        ...getScoreLabels('engagement', score),
        score,
        metric: formatPercentage(data.engagement_prc),
    };
};

const getTikTokPaidScore = (metric) => {
    if (!metric && metric !== 0) return null;
    if (metric < 0.01) return 9;
    if (metric < 0.05) return 7;
    if (metric < 0.2) return 5;
    if (metric < 0.5) return 3;
    return 1;
};

const getTikTokPaidPostsHealthStat = (data) => {
    if (!data) return null;
    const score = getTikTokPaidScore(
        data.partner_paid_partnership_posts_views_prc,
    );
    return {
        ...getScoreLabels('paid views', score),
        score,
        metric: formatPercentage(data.partner_paid_partnership_posts_views_prc),
    };
};

const getTikTokShopHealthStat = (data) => {
    if (!data) return null;
    const score = getTikTokPaidScore(
        data.eligible_for_commission_posts_views_prc,
    );
    return {
        ...getScoreLabels('TikTok shop', score),
        score,
        metric: formatPercentage(data.eligible_for_commission_posts_views_prc),
    };
};

const getPreciseConvergenceScore = (
    metric,
    metricLowerBound,
    metricUpperBound,
    scoreLowerBound,
    scoreUpperBound,
) => {
    return (
        (1 -
            (metric - metricLowerBound) /
                (metricUpperBound - metricLowerBound)) *
            (scoreUpperBound - scoreLowerBound) +
        scoreLowerBound
    );
};

const getConvergenceScore = (metric) => {
    if (!metric && metric !== 0) return null;
    if (metric > 2.6) return getPreciseConvergenceScore(metric, 2.6, 10, 0, 2);
    if (metric > 1.92)
        return getPreciseConvergenceScore(metric, 1.92, 2.6, 2, 4);
    if (metric > 1.42)
        return getPreciseConvergenceScore(metric, 1.42, 1.92, 4, 6);
    if (metric > 0.62)
        return getPreciseConvergenceScore(metric, 0.62, 1.42, 6, 8);
    return getPreciseConvergenceScore(metric, 0, 0.62, 8, 10);
};

const getSentimentHealthStat = (data) => {
    if (!data) return null;
    const score = data.sentimentScoreInt;
    return {
        ...getScoreLabels('sentiment score', score),
        score: formatHealthStat(score, true),
        metric:
            formatMetric(
                formatHealthStat(data.sentiment_score),
                'sentiment score',
            ) + ' / 10',
    };
};

export const getTikTokHealthStats = (data) => {
    if (!data) return null;
    return {
        'average weekly views': getTikTokViewsHealthStat(data),
        'paid views': getTikTokPaidPostsHealthStat(data),
        'sentiment score': getSentimentHealthStat(data),
        posts: getTikTokPostsHealthStat(data),
        'TikTok shop': getTikTokShopHealthStat(data),
        engagement: getEngagementHealthStat(data),
    };
};

// Related Categories / Brands consume health-stat scores precomputed in the
// API layer (see backend/api/api2/services/tiktok/related_categories/service.py).
// The FE only derives the user-facing label from the score — same pattern as
// IG's `hs_likes` flow.
const getTikTokRelatedAvgWeeklyViewsHealthStat = (data) => {
    if (!data) return null;
    const score = data.hs_avg_weekly_views;
    if (score === null || score === undefined) return null;
    return {
        ...getScoreLabels('average weekly views', score),
        score,
        metric: formatVolume(data.non_cum_avg_weekly_views),
    };
};

export const getTikTokRelatedHealthStats = (data) => {
    if (!data) return null;
    return {
        'average weekly views': getTikTokRelatedAvgWeeklyViewsHealthStat(data),
    };
};

// IG mirror of the TT pattern. BE precomputes `hs_avg_weekly_likes` from
// the new `data_store_*_related_*` tables; FE just derives the label.
const getInstagramRelatedAvgWeeklyLikesHealthStat = (data) => {
    if (!data) return null;
    const score = data.hs_avg_weekly_likes;
    if (score === null || score === undefined) return null;
    return {
        ...getScoreLabels('average weekly likes', score),
        score,
        metric: formatVolume(data.non_cum_avg_weekly_likes),
    };
};

export const getInstagramRelatedHealthStats = (data) => {
    if (!data) return null;
    return {
        'average weekly likes':
            getInstagramRelatedAvgWeeklyLikesHealthStat(data),
    };
};

const getPopularityScore = (metric) => {
    if (!metric && metric !== 0) return null;
    if (metric < 85_000) return 1;
    if (metric < 680_000) return 3;
    if (metric < 3_400_000) return 5;
    if (metric < 17_000_000) return 7;
    return 9;
};

const getPopularityHealthStat = (data) => {
    if (!data) return null;
    const score = getPopularityScore(data.popularity_index_avg_12_month);
    return {
        ...getScoreLabels('average monthly popularity', score),
        score,
        metric: formatVolume(data.popularity_index_avg_12_month),
    };
};

const getPoplarityShareHealthStat = (data) => {
    if (!data) return null;
    const percentageGoogle = data.google_share_prc || 0;
    const percentageTikTok = data.tiktok_share_prc || 0;
    const percentageInstagram = data.instagram_share_prc || 0;
    const maxPercentage = Math.max(
        percentageGoogle,
        percentageTikTok,
        percentageInstagram,
    );
    let score = null;
    if (percentageGoogle === maxPercentage) {
        score = 8;
    } else if (percentageTikTok === maxPercentage) {
        score = 7;
    } else if (percentageInstagram === maxPercentage) {
        score = 6;
    }

    return {
        ...getScoreLabels('popularity share', score),
        score,
        metric: formatPercentage(maxPercentage),
    };
};

const getPopularityPredictedGrowthHealthStat = (data) => {
    if (!data) return null;
    return {
        ...getScoreLabels('popularity predicted growth', data.pred_YoY_score),
        scoreLabel:
            data.pred_YoY_score_label || tooltips.healthStatsMissingScoreLabel,
        score: data.pred_YoY_score,
        metric: formatGrowth(data.predicted_YoY),
    };
};

const getPopularityConvergenceHealthStat = (data) => {
    if (!data) return null;
    const score = getConvergenceScore(data.indexed_convergence_score);
    return {
        ...getScoreLabels('popularity convergence', score),
        score: formatHealthStat(score, true),
        preciseScore: score,
        metric: formatMetric(
            formatHealthStat(score || score === 0 ? score / 10 : score),
            'popularity convergence',
        ),
    };
};

const getGrowthScore = (metric) => {
    if (!metric && metric !== 0) return null;
    if (metric < -0.5) return 1;
    if (metric < -0.15) return 3;
    if (metric < 0.15) return 5;
    if (metric < 0.5) return 7;
    return 9;
};

export const getPopularityRelatedEntityGrowthHealthStat = (data) => {
    if (!data) return null;
    const score = getGrowthScore(data.YoY);
    return {
        ...getScoreLabels('growth', score),
        score,
        metric: formatGrowth(data.YoY),
    };
};

export const getPopularityHealthStats = (data, price) => {
    if (!data) return null;
    return {
        'average monthly popularity': getPopularityHealthStat(data),
        'popularity share': getPoplarityShareHealthStat(data),
        'popularity convergence': getPopularityConvergenceHealthStat(data),
        'popularity predicted growth':
            getPopularityPredictedGrowthHealthStat(data),
        ...(price !== undefined && {
            'price point': getPriceSegmentationHealthStat(price),
        }),
    };
};

const getTikTokPaidPostsAllHealthStat = (data) => {
    if (!data) return null;
    const score = getTikTokPaidScore(data.paid_posts_views_prc_all);
    return {
        ...getScoreLabels('paid views: all time', score),
        score,
        metric: formatPercentage(data.paid_posts_views_prc_all),
    };
};

const getTikTokShopAllHealthStat = (data) => {
    if (!data) return null;
    const score = getTikTokPaidScore(
        data.eligible_for_commission_posts_views_prc_all,
    );
    return {
        ...getScoreLabels('TikTok shop: all time', score),
        score,
        metric: formatPercentage(
            data.eligible_for_commission_posts_views_prc_all,
        ),
    };
};

export const getCreatorHealthStats = (data) => {
    if (!data) return null;
    return {
        'engagement: all time': getEngagementHealthStat(data),
        'paid views: all time': getTikTokPaidPostsAllHealthStat(data),
        'TikTok shop: all time': getTikTokShopAllHealthStat(data),
    };
};

const emptySearchHealthStats = Array.from(
    new Set([...categoryHealthStats, ...brandHealthStats]),
).reduce((acc, key) => {
    acc[key] = createHealthStat(key, {});
    return acc;
}, {});

/**
 * Build the 'price point' health stat from a brand's own `price` column.
 *
 * Every data source's brand table carries price segmentation, so no page has
 * to derive it from a Google data point.
 */
export const getPriceSegmentationHealthStat = (price) =>
    priceSegmentationMap[price] || priceSegmentationMap.DEFAULT;

const scoreToPriceSegmentation = Object.entries(priceSegmentationMap).reduce(
    (acc, [price, { score }]) => {
        if (price !== 'DEFAULT') acc[score] = price;
        return acc;
    },
    {},
);

/** Inverse of the above: selected filters -> the literals the server takes. */
export const getPriceSegmentationFilter = (healthStatFilters) => {
    const prices = (healthStatFilters || [])
        .filter(({ metricLabel }) => metricLabel === 'price point')
        .map(({ score }) => scoreToPriceSegmentation[score])
        .filter(Boolean);
    return prices.length ? prices : undefined;
};

export const getTikTokAndSearchHealthStats = ({
    healthStatKeys,
    searchDataPoint,
    tikTokDataPoint,
    price,
}) => {
    if (!(searchDataPoint || tikTokDataPoint || price !== undefined))
        return null;
    const searchHealthStats =
        searchDataPoint?.healthStats || emptySearchHealthStats;
    const tikTokHealthStats = getTikTokHealthStats(tikTokDataPoint);
    const combinedHealthStats = {
        ...searchHealthStats,
        ...tikTokHealthStats,
        ...(price !== undefined && {
            'price point': getPriceSegmentationHealthStat(price),
        }),
    };
    return healthStatKeys.reduce((acc, key) => {
        if (combinedHealthStats.hasOwnProperty(key)) {
            acc[key] = combinedHealthStats[key];
        }
        return acc;
    }, {});
};

const getInstagramLikesHealthStat = (data) => {
    if (!data) return null;
    const score = data.hs_likes;
    return {
        ...getScoreLabels('ig posts', score),
        score,
        metric: formatVolume(data.likes),
    };
};

const getInstagramAvgWeeklyLikesHealthStat = (data) => {
    if (!data) return null;
    const score = data.hs_non_cum_avg_weekly_likes;
    return {
        ...getScoreLabels('average weekly likes', score),
        score,
        metric: formatVolume(data.non_cum_avg_weekly_likes),
    };
};

export const getInstagramHealthStats = (data) => {
    if (!data) return null;
    return {
        'average weekly likes': getInstagramAvgWeeklyLikesHealthStat(data),
        'ig posts': getInstagramLikesHealthStat(data),
    };
};
